import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function slugify(text: string | undefined | null): string {
  if (!text) {
    return '';
  }
  
  return text
    .toLowerCase()
    // Replace forward slashes with hyphens
    .replace(/\//g, '-')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove all characters that are not letters, numbers, or hyphens
    .replace(/[^\w-]+/g, '')
    // Replace multiple consecutive hyphens with a single hyphen
    .replace(/-+/g, '-')
    // Remove hyphens from the beginning and end
    .replace(/^-+|-+$/g, '');
}

export function calculateReadingTime(content: string): string {
  // Strip HTML tags
  const text = content.replace(/<[^>]*>/g, '');
  
  // Count words (split by spaces and filter out empty strings)
  const words = text.split(/\s+/).filter(Boolean).length;
  
  // Average reading speed (words per minute)
  const wpm = 225;
  
  // Calculate reading time in minutes
  const minutes = Math.ceil(words / wpm);
  
  return `${minutes} min read`;
}

interface TableOfContentsHeading {
  id: string;
  text: string;
  level: number;
}

interface DOMHeadingElement extends Element {
  textContent: string | null;
  id: string;
  tagName: string;
}

export async function generateTableOfContents(content: string) {
  // Return empty TOC if running in browser
  if (typeof window !== 'undefined') {
    return {
      toc: [],
      content
    };
  }

  // Server-side code
  const { JSDOM } = await import('jsdom');
  const dom = new JSDOM(content);
  const doc = dom.window.document;
  const headings = doc.querySelectorAll('h2, h3, h4');
  const toc: TableOfContentsHeading[] = [];

  headings.forEach((heading: DOMHeadingElement) => {
    const text = heading.textContent || '';
    const id = slugify(text);
    heading.id = id;
    
    const level = parseInt(heading.tagName.charAt(1)) - 1;
    toc.push({ id, text, level });
  });

  // Update the content with the new IDs
  const updatedContent = doc.body.innerHTML;

  return {
    toc,
    content: updatedContent
  };
}

export async function sanitizeHtml(content: string) {
  // Server-side sanitization
  if (typeof window === 'undefined') {
    try {
      const { JSDOM } = await import('jsdom');
      const dom = new JSDOM('');
      const DOMPurify = (await import('dompurify')).default(dom.window);
      return DOMPurify.sanitize(content, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li'],
        ALLOWED_ATTR: []
      });
    } catch (error) {
      console.error('Error sanitizing HTML on server:', error);
      return content;
    }
  }
  
  // Client-side sanitization
  try {
    const DOMPurify = (await import('dompurify')).default;
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: []
    });
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return content;
  }
}

/**
 * Decodes HTML entities in a string
 * @param html - The HTML string to decode
 * @returns The decoded string
 */
export function decodeHtmlEntities(html: string): string {
  if (!html) return '';
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * Decodes HTML entities in a string server-side
 * @param html - The HTML string to decode
 * @returns The decoded string
 */
export function decodeHtmlEntitiesServer(html: string): string {
  if (!html) return '';
  
  return html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#x60;/g, '`')
    .replace(/&#x3D;/g, '=');
}

/**
 * Transforms internal WordPress content URLs from the live site domain to the CMS domain.
 * Specifically targets /wp-content/ paths.
 * @param content - The string content containing URLs to transform
 * @returns The content with transformed URLs
 */
export function transformContentUrls(content: string): string {
  if (!content) return '';
  
  // Replace gptbot.io/wp-content/ with cms.gptbot.io/wp-content/
  const oldDomainPattern = new RegExp('https?://gptbot\.io/wp-content', 'g');
  const newDomain = 'https://cms.gptbot.io/wp-content';
  
  return content.replace(oldDomainPattern, newDomain);
}